import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { usePagination } from '@abyss/web/hooks/usePagination';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { Box } from '@abyss/web/ui/Box';
import { LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { Modal } from '@abyss/web/ui/Modal';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useHealthgradesGetBulkInfo } from '../../hooks/useHealthgrades';
import { useProviderDetailsData } from '../../hooks/useProviderDetailsData';
import { HealthGradesBulkInfo, Survey } from '../../models/Healthgrades';
import { ProviderType } from '../../models/ProviderDetails';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { getProviderId } from '../../utils/providerDetails.utils';
import { getNameAttrs } from '../../utils/providerNames.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../Constants';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { phoneOnly } from '../ConstantsStyles';
import { DetailsPageHeader } from '../DetailsPageHeader/DetailsPageHeader';
import { PatientReviewsModal } from '../ProviderDetailTabs/About/PatientReviews/PatientReviewModal';
import {
  AllPatientReviewsContainer,
  StyledIcon,
  StyledIconContainer,
  TextHeadingPatientReviews,
  modalStyles,
} from '../ProviderDetailTabs/About/PatientReviews/PatientReviews.styled';
import { convertObjectToUrlParams } from '../Utils';
import { handleLinkAndModalTrack } from '../Utils/adobeTrackUtils';
import { PatientReviewsSortOrder } from './AllPatientReviewsSortOrder';
import { BackButton } from './BackButton';
import { RatingsSection } from './RatingsSection';

export const AllPatientReviews = () => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const sectionHeader: string =
    Constants.PROVIDER_DETAILS.ABOUT_TAB.REVIEW_SECTION
      .WHAT_PATIENTS_ARE_SAYING;
  const patientReviewsDataTestId: string = 'patient-reviews-section';
  const { navigate } = useRouter();

  const {
    setDetailsStore,
    showDirection,
    avatar,
    inNetwork,
    providerName,
    primarySpeciality,
    professionalDesignation,
    rating,
    surveyCount,
    npi,
    imageLocation,
    providerDetails,
    storeProviderId,
    locationId,
  } = useProviderDetailsData();

  const providerId = getProviderId(storeProviderId);

  const chipStore = useChipStore(useShallow((state: ChipState) => state));

  const [navigateToDirections, setNavigateToDirections] = useState(
    Boolean(showDirection)
  );
  const providerLocations = providerDetails
    ? JSON.parse(providerDetails).providerLocations
    : {};
  const nameAttrs = getNameAttrs(
    providerDetails ? JSON.parse(providerDetails) : {}
  );
  const [reviewModal, setReviewModal] = useState<Survey | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [healthgradesReview, setHealthgradesReview] =
    useState<HealthGradesBulkInfo>({});
  const [, healthgradesGetBulkInfo] = useHealthgradesGetBulkInfo({
    onCompleted: (result) => {
      setIsLoading(false);
      if (result?.data?.healthgradesGetBulkInfo) {
        setHealthgradesReview(result.data.healthgradesGetBulkInfo[0]);
      }
    },
    onError: (error) => {
      setIsLoading(false);
      throw error;
    },
  });

  const [sortOrder, setSortOrder] = useState<PatientReviewsSortOrder>(
    PatientReviewsSortOrder.MOST_RECENT
  );
  const [pageSize, setPageSize] = useState<number>(10);
  const { gotoPage, state, ...paginationProps } = usePagination({
    pages: Math.ceil((healthgradesReview?.surveys?.length || 0) / pageSize),
  });
  const { currentPage, pageIndex, pageCount } = state;

  const [isOpen, setIsOpen] = useState(false);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: 'about:patient reviews',
    sitesectionLevel1: Constants.ADOBE_TRACKING.DETAILS_SITESECTION1,
    sitesectionLevel2: `provider details`,
    impressionBlock: {
      type: 'provider',
      indicator: '',
    },
  });

  useEffect(() => {
    if (showDirection) {
      setNavigateToDirections(true);
    }
  }, [showDirection]);

  useEffect(() => {
    if (navigateToDirections) {
      const detailsStoreValues = {
        showDirection: navigateToDirections,
      };
      setDetailsStore(detailsStoreValues);
      const urlParams = convertObjectToUrlParams(chipStore, {
        providerId,
        locationId,
        ...detailsStoreValues,
      });
      navigate(`${ConstantsRoutes.PROVIDER_DETAILS.path}${urlParams}`);
    }
  }, [navigateToDirections]);

  useEffect(() => {
    adobeLinkTrackEvent({
      name: `results per page:${pageSize}`,
      location: `body:pagination control:page ${currentPage}`,
    });
  }, [pageSize, currentPage]);

  useEffect(() => {
    if (reviewModal) {
      const linkName: string = patientReviewsDataTestId;
      const linkLocation: string = `body:${sectionHeader}:page ${currentPage}`;
      const modalName: string = 'patient review';
      handleLinkAndModalTrack(linkName, linkLocation, modalName);
    }
  }, [reviewModal]);

  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  useEffect(() => {
    if (npi) {
      setIsLoading(true);
      healthgradesGetBulkInfo({
        variables: {
          npis: [npi],
        },
      });
    }
  }, [npi]);
  const handleOnClick = () => {
    handleLinkAndModalTrack(
      `all-patient-reviews-sort-order`,
      `modal:all patient reviews sort`,
      'all patient reviews sort'
    );
    setIsOpen(true);
  };

  return (
    <div>
      <Box
        color="#FBFCFE"
        css={{
          'abyss-box-root': {
            height: 'auto',
            padding: '0px',
            borderBottom:
              '1px solid  var(--abyss-psx-parcel-colors-cardBorder)',
          },
        }}
      >
        <DetailsPageHeader
          avatar={avatar}
          hideRating
          imageLocation={imageLocation}
          inNetwork={inNetwork}
          nameAttrs={nameAttrs}
          primarySpeciality={primarySpeciality?.split('|')}
          professionalDesignation={professionalDesignation}
          providerId={providerId}
          providerLocations={providerLocations}
          providerName={providerName}
          providerType={ProviderType.PRACTITIONER}
          rating={rating}
          setNavigateToDirections={setNavigateToDirections}
          surveyCount={surveyCount}
        />
      </Box>
      <Modal
        closeOnClickOutside={false}
        css={modalStyles}
        isOpen={reviewModal !== null}
        onClose={() => setReviewModal(null)}
        title={t('Full Review')}
      >
        <Modal.Section
          data-auto-testId={patientReviewsDataTestId}
          data-testId={patientReviewsDataTestId}
        >
          <PatientReviewsModal review={reviewModal} />
        </Modal.Section>
      </Modal>
      <AllPatientReviewsContainer>
        {!mobileScreen && <BackButton />}
        <StyledIconContainer>
          <StyledIcon
            icon="healthgrades"
            isScreenReadable
            size={132}
            title={t('HEALTH_GRADES_LOGO_TITLE')}
          />
        </StyledIconContainer>
        {!mobileScreen && (
          <div style={{ marginBottom: '12px', marginTop: '12px' }}>
            <TextHeadingPatientReviews>
              {t('PROVIDER_DETAILS.ABOUT_TAB.REVIEW_SECTION.PATIENT_REVIEWS')}
            </TextHeadingPatientReviews>
          </div>
        )}
        {isLoading ? (
          <LoadingSpinner
            ariaLoadingLabel="loading results"
            isLoading={isLoading}
            size="$sm"
          />
        ) : (
          <RatingsSection
            currentPage={currentPage}
            gotoPage={gotoPage}
            handleOnClick={handleOnClick}
            healthgradesReview={healthgradesReview}
            isOpen={isOpen}
            pageCount={pageCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            paginationProps={paginationProps}
            setIsOpen={setIsOpen}
            setPageSize={setPageSize}
            setReviewModal={setReviewModal}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
          />
        )}
      </AllPatientReviewsContainer>
    </div>
  );
};
